import React, { useMemo, useState } from 'react';
import { Search, CalendarIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

const NotificationSearch = ({ searchQuery, onSearchChange, dateRange, onDateChange, onClearFilters }) => {
   const [isCalendarOpen, setIsCalendarOpen] = useState(false);
   // Browser detection
   const isChrome = useMemo(() => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isBrave = navigator.brave !== undefined;
      return userAgent.includes('chrome') && !userAgent.includes('edg') && !isBrave;
   }, []);

   const DateRangeComponent = () => {
      if (isChrome) {
         return (
            <div className="flex items-center space-x-2">
               <div className="flex items-center rounded-md border border-input">
                  <input
                     type="date"
                     value={dateRange.from ? format(dateRange.from, "yyyy-MM-dd") : ""}
                     onChange={(e) => {
                        const from = e.target.value ? new Date(e.target.value) : undefined;
                        onDateChange({ ...dateRange, from });
                     }}
                     className="h-10 px-3 py-2 rounded-md bg-background text-sm focus:outline-none"
                  />
               </div>
               <span className="text-sm text-muted-foreground">to</span>
               <div className="flex items-center rounded-md border border-input">
                  <input
                     type="date"
                     value={dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : ""}
                     onChange={(e) => {
                        const to = e.target.value ? new Date(e.target.value) : undefined;
                        onDateChange({ ...dateRange, to });
                     }}
                     className="h-10 px-3 py-2 rounded-md bg-background text-sm focus:outline-none"
                  />
               </div>
            </div>
         );
      }

      return (
         <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
               <Button
                  variant="outline"
                  className={`w-[225px] justify-start text-left font-normal ${
                     dateRange.from ? 'text-foreground' : 'text-muted-foreground'
                  }`}
               >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                     dateRange.to ? (
                        <>
                           {format(dateRange.from, "MM/dd/yyyy")} -{" "}
                           {format(dateRange.to, "MM/dd/yyyy")}
                        </>
                     ) : (
                        format(dateRange.from, "MM/dd/yyyy")
                     )
                  ) : (
                     <span>Select date range</span>
                  )}
               </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
               <Calendar
                  initialFocus
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                     onDateChange(range || { from: undefined, to: undefined });
                     // Only close if both dates are selected or the selection is cleared
                     if ((range?.from && range?.to) || !range) {
                        setIsCalendarOpen(false);
                     }
                  }}
                  numberOfMonths={2}
               />
            </PopoverContent>
         </Popover>
      );
   };

   return (
      <div className="flex justify-between items-center gap-4">
         <div className="relative w-[300px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
               type="text"
               placeholder="Search notifications..."
               value={searchQuery}
               onChange={(e) => onSearchChange(e.target.value)}
               className="pl-9 w-full"
            />
         </div>
         <div className="flex items-center gap-2">
            <DateRangeComponent />
            <Button
               variant="outline"
               onClick={onClearFilters}
               className="h-10"
            >
               Clear
            </Button>
         </div>
      </div>
   );
};

export default NotificationSearch;
